<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { ApiError, authApi } from '../api'
import AppMark from '../components/AppMark.vue'
import { navigate } from '../router'
import { acceptAuth, useSession } from '../session'

const session = useSession()
const loading = ref(false)
const botUrl = ref('')
const nonce = ref('')
const error = ref('')
const expired = ref(false)
let pollTimer = 0

async function prepareLogin(): Promise<void> {
  loading.value = true
  error.value = ''
  expired.value = false
  window.clearTimeout(pollTimer)
  try {
    const login = await authApi.start()
    nonce.value = login.nonce
    botUrl.value = login.botUrl
    pollTimer = window.setTimeout(poll, 1_000)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось начать вход.'
  } finally {
    loading.value = false
  }
}

async function poll(): Promise<void> {
  if (!nonce.value) return
  try {
    const result = await authApi.poll(nonce.value)
    if (result.status === 'authenticated') {
      acceptAuth(result)
      navigate(result.kind === 'admin' ? '/admin' : '/', true)
      return
    }
    if (result.status === 'expired' || result.status === 'used') {
      expired.value = true
      if (result.status === 'used') {
        error.value = 'Эта ссылка уже использована. Получите новую, чтобы войти в этом окне.'
      }
      return
    }
    pollTimer = window.setTimeout(poll, 1_500)
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 429) {
      pollTimer = window.setTimeout(poll, 3_000)
      return
    }
    error.value = cause instanceof Error ? cause.message : 'Не удалось проверить вход.'
    pollTimer = window.setTimeout(poll, 3_000)
  }
}

function openBot(): void {
  if (!botUrl.value) return
  window.open(botUrl.value, '_blank', 'noopener,noreferrer')
}

onMounted(() => {
  if (session.state.authenticated) {
    navigate(session.state.kind === 'admin' ? '/admin' : '/', true)
    return
  }
  void prepareLogin()
})

onBeforeUnmount(() => window.clearTimeout(pollTimer))
</script>

<template>
  <main class="login-page">
    <div class="login-page__glow login-page__glow--one"></div>
    <div class="login-page__glow login-page__glow--two"></div>

    <section class="login-card">
      <a href="/" class="brand brand--large">
        <AppMark />
        <span>GetSlides</span>
      </a>

      <div class="login-card__hero">
        <span class="eyebrow">Презентация начинается с мысли</span>
        <h1>Расскажите идею.<br>Слайды соберутся сами.</h1>
        <p>
          Живой текст, аккуратная композиция и готовый режим выступления —
          без ночной борьбы с прямоугольниками.
        </p>
      </div>

      <div class="login-card__action">
        <button
          type="button"
          class="button button--primary button--large button--wide"
          :disabled="loading || !botUrl"
          @click="openBot"
        >
          <span class="telegram-dot" aria-hidden="true">↗</span>
          {{ loading ? 'Готовим вход…' : 'Войти через Telegram' }}
        </button>
        <p class="login-hint">
          Бот подтвердит вход одним нажатием. Паролей здесь нет.
        </p>
        <p v-if="botUrl && !expired" class="login-wait">
          <span class="status-pulse"></span>
          Ждём подтверждения в Telegram
        </p>
        <button v-if="expired" type="button" class="text-button" @click="prepareLogin">
          Ссылка устарела — получить новую
        </button>
        <p v-if="error" class="form-error">{{ error }}</p>
      </div>
    </section>

    <aside class="login-preview" aria-hidden="true">
      <div class="fake-deck">
        <div class="fake-deck__chrome">
          <span></span><span></span><span></span>
          <i>GetSlides</i>
        </div>
        <div class="fake-deck__slide">
          <small>ВАША ИДЕЯ · 2026</small>
          <strong>Всё важное<br>помещается<br>на одном экране.</strong>
          <div class="fake-deck__line"></div>
          <span>01 / 08</span>
        </div>
      </div>
    </aside>
  </main>
</template>
